import { ref } from 'vue';
import { defineStore } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useEditor } from '@/stores/editor/editor';
import { useTracking } from '@/stores/tracking/tracking';
import { generateAiSuggestions } from '@/api/dfd';

export const useDocumentAiSuggestion = defineStore('documentAiSuggestion', () => {
  const editorStore = useEditor();

  const isLoading = ref(false);
  const aiSuggestions = ref({});

  const generateTooltipSuggestions = async ({
    id, ruleId, text, className,
  }) => {
    if (!text) {
      useAppSnackbar().addInfoSnackbar({ text: 'Page content updated. Please close the tooltip and try again.' });
      return null;
    }

    try {
      isLoading.value = true;

      // Track the action
      useTracking().applyHighlight({
        job: editorStore.document,
        props: {
          action: 'get_suggestions',
          replacement: '',
          removedText: text,
          color: className,
          suggestions: [],
        },
      });

      const data = await generateAiSuggestions({
        jobId: editorStore.jobId,
        ruleId,
        text,
      });

      // Update with the new suggestions
      aiSuggestions.value[id] = data.suggestions.map((suggestion) => ({ text: suggestion }));

      // Track successful generation
      useTracking().applyHighlight({
        job: editorStore.document,
        props: {
          action: 'ai_suggested',
          replacement: '',
          removedText: text,
          color: className,
          suggestions: data.suggestions,
        },
      });

      return data;
    } catch (error) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to generate suggestions - ${error.message}` });
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    aiSuggestions,
    generateTooltipSuggestions,
  };
});
